/**
 * @file src/room/handDraw.js
 * @description The deterministic per-section card draw — a room's HAND.
 *
 * A scored room does not put the whole pinned collection on the card rail. Each section deals a small
 * hand (by default 2 tracks, 2 orbiters, 2 worlds), and BOTH players must see the SAME six without a
 * single byte crossing the wire. So the hand is a pure function of things every participant already
 * knows identically: the room id, the score's own section ids, the section pointer, and a draw index.
 * Nothing here reads room state, and nothing here imports the room client — same inputs, same output,
 * on every device, forever.
 *
 * The model, in the order it matters:
 *
 *   - ONE PILE PER KIND. Tracks, orbiters and worlds each get their own pile, shuffled independently.
 *     A kind the collection has none of simply contributes nothing — it is never topped up from
 *     another kind, because a hand of six tracks is a different game from two of each.
 *   - THE PILE IS THE GRANT'S PINNED IDS, SORTED BY ID. Sorting is what makes the input order
 *     irrelevant: the rail can be reordered per user and the collection read can come back in any
 *     order, and the shuffle still starts from the same list on both machines.
 *   - DEALT WITHOUT REPLACEMENT. A pointer walks the pile; a card does not come back — played or
 *     not — until the pile runs out, and only then does that pile reshuffle. The pointer position is
 *     ARITHMETIC from the section index (section 0 takes cards 0–1, section 1 takes 2–3), never a
 *     counter that ticks as the room plays: a counter would drift the moment one client missed a
 *     section boundary or joined late.
 *   - THE SEED IS `hash(roomId, sectionId, drawIndex)`, plus the kind so the piles shuffle
 *     independently. `sectionId` — the section's ID, not its position — because a score edited
 *     between rooms must not silently re-deal a room that is already in flight. Each time a pile is
 *     exhausted and reshuffled, the section that OPENS that new run supplies the id, so a run of
 *     sections shares one pile ordering (which is what makes "without replacement" mean anything)
 *     while every reshuffle still gets a fresh, section-identified seed.
 *   - `drawIndex` is the re-deal counter. The dice control moves it — folded once by the relay and
 *     carried in the score transport — and moving it re-deals the hand for the same section without
 *     touching anything else here.
 */

/** The pile kinds, spelled as the collection roster spells them (a world is an `entangled-world`). */
export const HAND_KINDS = ['track', 'orbiter', 'entangled-world'];

/** The hand a room deals when its grant carries no composition of its own. */
export const DEFAULT_HAND_COMPOSITION = Object.freeze({ track: 5, orbiter: 1, 'entangled-world': 1 });

/**
 * Normalize whatever the room grant carries into a usable `{ enabled, composition }`.
 *
 * The draw is OFF unless a room explicitly says it is on. Any per-kind count that is not a
 * non-negative whole number falls back to that kind's default — a nonsense number for tracks must
 * not also take the orbiters down with it.
 *
 * "Nobody told us" MUST NOT mean "deal a hand". A room whose draw never arrived — a relay that
 * predates this feature, a collection read made without a grant, an expired grant — would otherwise
 * fall through to `enabled: true` AND to each client's own release-filtered roster as the pool. Two
 * clients whose rosters differ by one archived track would then deal genuinely different hands, in
 * silence, which is the single failure this whole module exists to prevent. Dealing the whole album
 * is always a safe answer; dealing a hand nobody agreed on is not.
 *
 * This is also the answer mycelium's model gives (`draw.enabled` defaults false) and the one root's
 * `readDrawConfig` gives (null for an absent field), so the three ends agree on what absent means.
 *
 * @param {{enabled?:boolean, composition?:Record<string, number>}|null|undefined} raw
 * @returns {{enabled:boolean, composition:Record<string, number>}}
 */
export function resolveDrawConfig(raw) {
  const source = raw && typeof raw === 'object' && raw.composition && typeof raw.composition === 'object'
    ? raw.composition
    : null;
  const composition = {};
  for (const kind of HAND_KINDS) {
    const count = Number(source?.[kind]);
    composition[kind] = Number.isInteger(count) && count >= 0 ? count : DEFAULT_HAND_COMPOSITION[kind];
  }
  return { enabled: raw?.enabled === true, composition };
}

/**
 * A pile kind, however the source happened to spell it — or null for anything that is not one.
 *
 * Three sources name these kinds and only two of them agree: the grant's roster and the room wire
 * say `entangled-world`, while a good deal of the app says `world`. The piles are keyed on ONE
 * spelling, so the translation lives here, beside the piles, rather than once per caller.
 */
export function drawKind(raw) {
  const value = String(raw ?? '').toLowerCase();
  if (value === 'track' || value === 'orbiter') return value;
  if (value === 'world' || value === 'entangled-world') return 'entangled-world';
  return null;
}

/**
 * The pinned pool as `[{ id, kind }]`, from a collection payload or from the room's join snapshot.
 *
 * Both have to normalize to the same thing, because they describe the same list: the creator reads
 * it from the grant and states it at `room:create`, and every joiner reads it back out of
 * `room:joined`. A room where one participant's pool normalized differently from another's is a
 * room where the two deal different hands, which is the one thing the draw exists to prevent.
 * Entries with no usable id or kind are dropped, and a duplicate contributes one card, not two.
 */
export function normalizeDrawPinned(raw) {
  if (!Array.isArray(raw)) return [];
  const pinned = [];
  const seen = new Set();
  for (const item of raw) {
    const id = typeof item?.id === 'string' ? item.id.trim() : (typeof item?.entityId === 'string' ? item.entityId.trim() : '');
    const kind = drawKind(item?.kind ?? item?.entityType);
    if (!id || !kind) continue;
    const key = `${kind}:${id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    pinned.push({ id, kind });
  }
  return pinned;
}

/** FNV-1a over the joined parts, joined on a separator no entity id can contain — so `("ab","c")`
 *  and `("a","bc")` can never hash to the same seed. */
function hashSeed(parts) {
  const text = parts.join('\u001f');
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

/** mulberry32 — a tiny, exactly-reproducible PRNG. Integer math only, so every engine agrees. */
function mulberry32(seed) {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Fisher-Yates driven by the seeded PRNG — the same permutation for the same seed, everywhere. */
function shuffled(ids, seed) {
  const out = ids.slice();
  const random = mulberry32(seed);
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    const swap = out[i];
    out[i] = out[j];
    out[j] = swap;
  }
  return out;
}

/**
 * The section that opens run `run` of a pile — the one whose id seeds that run's shuffle.
 *
 * Run `run` covers the pile positions `[run * pileLength, (run + 1) * pileLength)`, and section `i`
 * reads from position `i * take`, so the first section to reach into the run is `ceil(run * pileLength / take)`.
 * A run opened beyond the end of the score (only reachable when a client is asked for a section the
 * score does not have) falls back to the score's last id, distinguished by the run number so the
 * reshuffle is still a reshuffle.
 */
function runSeedSectionId(sectionIds, run, pileLength, take) {
  const opener = Math.ceil((run * pileLength) / take);
  const id = sectionIds[opener];
  if (typeof id === 'string' && id !== '') return id;
  const last = sectionIds.length > 0 ? sectionIds[sectionIds.length - 1] : '';
  return `${last}#${run}`;
}

/** The pinned ids of one kind: de-duplicated and SORTED, so the pile never depends on input order. */
function pileFor(pinned, kind) {
  const ids = new Set();
  for (const item of pinned) {
    if (item && item.kind === kind && typeof item.id === 'string' && item.id !== '') ids.add(item.id);
  }
  return [...ids].sort();
}

/**
 * The order run `run` of a pile deals in — the seeded shuffle, ADJUSTED at the reshuffle boundary.
 *
 * A hand that straddles a reshuffle holds cards from the previous run's tail, and the fresh shuffle
 * is free to open with exactly those cards — dealing one twice in a single hand, or handing the very
 * next section a card the player was just offered. So the adjustment happens HERE, in the order
 * itself: cards from the previous run's last `take` (everything the boundary-adjacent section can
 * hold) are swept out of the new order's first `2·take − 1` positions (the straddle remainder plus
 * the whole next hand), each swapped with the first later card outside that tail. Both orders derive
 * from seeds alone, so every client computes the identical adjustment with no state — and each
 * section's walk stays pure position arithmetic.
 *
 * A pile too small to fully separate tail from opening is adjusted as far as its clean cards reach;
 * the walk's duplicate guard covers what genuinely cannot be separated.
 */
function orderForRun({ pile, run, take, roomId, sectionIds, drawIndex, kind }) {
  const pileLength = pile.length;
  const seedFor = (r) => hashSeed([roomId, runSeedSectionId(sectionIds, r, pileLength, take), String(drawIndex), kind]);
  const base = shuffled(pile, seedFor(run));
  // Run 0 has no boundary behind it; take 1 cannot straddle; a pile the hand consumes whole has no
  // cards outside the tail to swap in.
  if (run === 0 || take <= 1 || pileLength === take) return base;
  // The ADJUSTED previous order, recursively: the previous run's own boundary swaps moved cards into
  // its tail, and it is that dealt tail — not the base shuffle's — this run must open clear of.
  const previous = orderForRun({ pile, run: run - 1, take, roomId, sectionIds, drawIndex, kind });
  // Both reaches are 2·take − 1: the previous run's last positions sit in the boundary section AND
  // its predecessor, and the new order's opening positions sit in the boundary section AND its
  // successor — every pair of ADJACENT sections around the boundary is covered.
  const tail = new Set(previous.slice(Math.max(0, pileLength - (2 * take - 1))));
  const window = Math.min(2 * take - 1, pileLength);
  const out = base.slice();
  for (let i = 0; i < window && i < out.length; i += 1) {
    if (!tail.has(out[i])) continue;
    let j = Math.max(window, i + 1);
    while (j < out.length && tail.has(out[j])) j += 1;
    if (j >= out.length) break; // out of clean cards — the rest is genuinely unseparable
    const swap = out[i];
    out[i] = out[j];
    out[j] = swap;
  }
  return out;
}

/**
 * Deal one kind's share of the hand.
 *
 * Walks the pile from `sectionIndex * take`, crossing into the next (freshly reshuffled, boundary-
 * adjusted) run when the pile is exhausted. The duplicate guard is belt-and-braces for piles too
 * small for the adjustment to fully separate a boundary (see `orderForRun`).
 */
function dealKind({ pinned, kind, count, roomId, sectionIds, sectionIndex, drawIndex }) {
  const pile = pileFor(pinned, kind);
  const pileLength = pile.length;
  const take = Math.min(count, pileLength);
  if (take <= 0) return [];

  let run = Math.floor((sectionIndex * take) / pileLength);
  let position = (sectionIndex * take) % pileLength;
  let order = orderForRun({ pile, run, take, roomId, sectionIds, drawIndex, kind });

  const hand = [];
  // Bounded by construction: each step consumes one pile position and `take <= pileLength`, so the
  // walk can cross at most one reshuffle. The cap is belt-and-braces against a malformed input.
  for (let step = 0; hand.length < take && step < pileLength * 2; step += 1) {
    const id = order[position];
    if (!hand.includes(id)) hand.push(id);
    position += 1;
    if (position >= pileLength) {
      position = 0;
      run += 1;
      order = orderForRun({ pile, run, take, roomId, sectionIds, drawIndex, kind });
    }
  }
  return hand;
}

/**
 * The hand for one section of one room.
 *
 * @param {object} args
 * @param {Array<{id:string, kind:string}>} args.pinned The grant's pinned entities — the pool the
 *   piles are built from. NOT the collection read (which re-filters by release readiness, so an
 *   entity archived mid-room would shorten one player's pool and not the other's) and NOT the card
 *   rail's order (which each player reorders for themselves).
 * @param {Record<string, number>} args.composition How many of each kind a hand holds.
 * @param {string} args.roomId The room's own id, minted per room and on every participant's boot URL.
 * @param {string[]} args.sectionIds The score's section ids, in score order. `sectionIds[sectionIndex]`
 *   is the live section; the rest are what let a reshuffle name the section that opened it.
 * @param {number} args.sectionIndex Which section is playing — the pile pointer's only input.
 * @param {number} [args.drawIndex] The re-deal counter for this section (0 = the first deal).
 * @returns {{ids:string[], byKind:Record<string, string[]>}} The hand's entity ids, flat and per kind.
 */
export function drawHand({ pinned, composition, roomId, sectionIds, sectionIndex, drawIndex = 0 }) {
  const pool = Array.isArray(pinned) ? pinned : [];
  const ids = Array.isArray(sectionIds) ? sectionIds : [];
  const index = Number.isInteger(sectionIndex) && sectionIndex >= 0 ? sectionIndex : 0;
  const draw = Number.isInteger(drawIndex) && drawIndex >= 0 ? drawIndex : 0;
  const counts = composition && typeof composition === 'object' ? composition : DEFAULT_HAND_COMPOSITION;

  const byKind = {};
  const flat = [];
  for (const kind of HAND_KINDS) {
    const count = Number.isInteger(counts[kind]) && counts[kind] >= 0 ? counts[kind] : 0;
    const dealt = dealKind({
      pinned: pool,
      kind,
      count,
      roomId: typeof roomId === 'string' ? roomId : '',
      sectionIds: ids,
      sectionIndex: index,
      drawIndex: draw,
    });
    byKind[kind] = dealt;
    flat.push(...dealt);
  }
  return { ids: flat, byKind };
}
